import {isValidPhoneNumber, parsePhoneNumber} from 'libphonenumber-js';

const EMAIL_REGEX = /^(?!.*\.\.)(?!.*\.$)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
const NAME_REGEX = /^[\p{L}\s'-]+$/u;
const NAME_MIN_LENGTH = 1;
const NAME_MAX_LENGTH = 255;
const EMAIL_MAX_LENGTH = 255;
const QUESTION_ANSWER_MIN_COUNT = 1;
const QUESTION_ANSWER_MAX_COUNT = 3;
const MIN_QUESTION_LENGTH = 1;
const MIN_ANSWER_LENGTH = 1;

export interface ValidationResult {
    isValid: boolean;
    errorMessage?: string;
}

export interface QnAChallenge {
    question: string;
    answer: string;
}

export interface RecipientFormData {
    email: string;
    first_name: string;
    last_name: string;
    title?: string;
    organisation?: string;
    personal_message?: string;
}

export const getIsValidEmail = (email: string): boolean => {
    if (!email || typeof email !== 'string') {
        return false;
    }
    const trimmedEmail = email.trim();
    return trimmedEmail.length <= EMAIL_MAX_LENGTH && EMAIL_REGEX.test(trimmedEmail);
};

export const getIsValidName = (name: string | null | undefined): boolean => {
    if (!name || typeof name !== 'string') {
        return false;
    }
    const trimmedName = name.trim();
    return trimmedName.length >= NAME_MIN_LENGTH &&
           trimmedName.length <= NAME_MAX_LENGTH &&
           NAME_REGEX.test(trimmedName);
};

export const getIsValidOrganisation = (organisation: string | null | undefined): boolean => {
    if (!organisation) {
        return true;
    }
    if (typeof organisation !== 'string') {
        return false;
    }
    const trimmedOrganisation = organisation.trim();
    return trimmedOrganisation.length <= NAME_MAX_LENGTH;
};

export const getEmailValidationResult = (email: string, errorMessage: string): ValidationResult => {
    const isValid = getIsValidEmail(email);
    return {
        isValid,
        errorMessage: isValid ? undefined : errorMessage
    };
};

export const getNameValidationResult = (name: string | null | undefined, errorMessage: string): ValidationResult => {
    const isValid = getIsValidName(name);
    return {
        isValid,
        errorMessage: isValid ? undefined : errorMessage
    };
};

export const getOrganisationValidationResult = (
    organisation: string | null | undefined,
    errorMessage: string
): ValidationResult => {
    const isValid = getIsValidOrganisation(organisation);
    return {
        isValid,
        errorMessage: isValid ? undefined : errorMessage
    };
};

export const validateEmail = (email: string): ValidationResult => {
    if (!email || email.trim().length === 0) {
        return {
            isValid: false,
            errorMessage: 'Email is required'
        };
    }

    const trimmedEmail = email.trim();

    if (trimmedEmail.length > EMAIL_MAX_LENGTH) {
        return {
            isValid: false,
            errorMessage: `Email must not exceed ${EMAIL_MAX_LENGTH} characters`
        };
    }

    if (!EMAIL_REGEX.test(trimmedEmail)) {
        return {
            isValid: false,
            errorMessage: 'Email format is invalid (RFC 5322)'
        };
    }

    return {isValid: true};
};

export const validateName = (name: string, fieldName: string = 'Name'): ValidationResult => {
    if (!name || name.trim().length === 0) {
        return {
            isValid: false,
            errorMessage: `${fieldName} is required`
        };
    }

    const trimmedName = name.trim();

    if (trimmedName.length > NAME_MAX_LENGTH) {
        return {
            isValid: false,
            errorMessage: `${fieldName} must not exceed ${NAME_MAX_LENGTH} characters`
        };
    }

    if (!NAME_REGEX.test(trimmedName)) {
        return {
            isValid: false,
            errorMessage: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`
        };
    }

    return {isValid: true};
};

export const validatePhone = (phoneNumber: string): ValidationResult => {
    if (!phoneNumber || phoneNumber.trim().length === 0) {
        return {
            isValid: false,
            errorMessage: 'Phone number is required'
        };
    }

    const trimmedPhone = phoneNumber.trim();

    if (!isValidPhoneNumber(trimmedPhone)) {
        return {
            isValid: false,
            errorMessage: 'Phone number must be in valid international format (e.g., +1234567890)'
        };
    }

    try {
        const parsedPhone = parsePhoneNumber(trimmedPhone);
        if (!parsedPhone.isPossible()) {
            return {
                isValid: false,
                errorMessage: 'Phone number is not a valid phone number'
            };
        }
    } catch (error) {
        return {
            isValid: false,
            errorMessage: 'Phone number format is invalid'
        };
    }

    return {isValid: true};
};

export const validateQnAChallenges = (challenges: QnAChallenge[]): ValidationResult => {
    if (!challenges || challenges.length < QUESTION_ANSWER_MIN_COUNT) {
        return {
            isValid: false,
            errorMessage: `At least ${QUESTION_ANSWER_MIN_COUNT} question-answer pair is required`
        };
    }

    if (challenges.length > QUESTION_ANSWER_MAX_COUNT) {
        return {
            isValid: false,
            errorMessage: `Maximum ${QUESTION_ANSWER_MAX_COUNT} question-answer pairs allowed`
        };
    }

    for (let i = 0; i < challenges.length; i++) {
        const challenge = challenges[i];

        if (!challenge.question || challenge.question.trim().length < MIN_QUESTION_LENGTH) {
            return {
                isValid: false,
                errorMessage: `Question ${i + 1} is required`
            };
        }

        if (!challenge.answer || challenge.answer.trim().length < MIN_ANSWER_LENGTH) {
            return {
                isValid: false,
                errorMessage: `Answer ${i + 1} is required`
            };
        }
    }

    return {isValid: true};
};

export const validateRecipientForm = (formData: RecipientFormData): ValidationResult => {
    const firstNameValidation = validateName(formData.first_name, 'First name');
    if (!firstNameValidation.isValid) {
        return firstNameValidation;
    }

    const lastNameValidation = validateName(formData.last_name, 'Last name');
    if (!lastNameValidation.isValid) {
        return lastNameValidation;
    }

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
        return emailValidation;
    }

    return {isValid: true};
};

export const getIsPhoneValid = (phone: string): boolean => {
    return validatePhone(phone).isValid;
};

export const getIsQnAChallengesValid = (challenges: QnAChallenge[]): boolean => {
    return validateQnAChallenges(challenges).isValid;
};

export const getHasValidationErrors = (formData: RecipientFormData): boolean => {
    return !validateRecipientForm(formData).isValid;
};
